import { Table, Space, Tag, message, Popconfirm, Button } from 'antd';
import { useWisecureQuery, useWisecureMutation, useInvalidateQueries } from '@wisecure/api-client/hooks';
import { queryKeys } from '@/api/query-keys';
import { PageHeader } from '@/components/PageHeader';
import type { PaginatedResponse, DashboardUserListItem } from '@/types/dashboard';
import { error as errorColors, info } from '@/theme';

export function UsersPage() {
  const invalidate = useInvalidateQueries();

  const { data: usersResponse, isLoading } = useWisecureQuery<PaginatedResponse<DashboardUserListItem>>(
    queryKeys.dashboardUsers,
    (client) =>
      client.dashboard.dashboardControllerListUsers({}) as Promise<PaginatedResponse<DashboardUserListItem>>,
  );

  const deleteMutation = useWisecureMutation<unknown, string>(
    (client, id) => client.users.usersControllerRemove({ id }),
    {
      onSuccess: () => {
        message.success('Utilisateur supprimé.');
        invalidate(queryKeys.dashboardUsers);
      },
      onError: () => {
        message.error("Erreur lors de la suppression de l'utilisateur.");
      },
    },
  );

  const columns = [
    {
      title: 'Nom',
      key: 'name',
      render: (_: unknown, record: DashboardUserListItem) =>
        [record.firstName, record.lastName].filter(Boolean).join(' ') || '—',
    },
    { title: 'E-mail', dataIndex: 'email', key: 'email' },
    {
      title: 'Rôle',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => (
        <Tag
          style={{
            backgroundColor: `${role === 'ADMIN' ? errorColors[400] : info[400]}14`,
            color: role === 'ADMIN' ? errorColors[400] : info[400],
            borderColor: `${role === 'ADMIN' ? errorColors[400] : info[400]}40`,
            borderRadius: 100,
          }}
        >
          {role}
        </Tag>
      ),
    },
    {
      title: 'Créé le',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleDateString('fr-FR'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: DashboardUserListItem) => (
        <Space>
          <Popconfirm title="Supprimer l'utilisateur ?" onConfirm={() => deleteMutation.mutate(record.id)}>
            <Button danger size="small">
              Supprimer
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <PageHeader title="Utilisateurs" />
      <Table columns={columns} dataSource={usersResponse?.data ?? []} loading={isLoading} rowKey="id" />
    </div>
  );
}
